/**
 * @file `Recalibration` — namespacer for `admin.recalibration.*`,
 *       backing the `/recalibration` panel in the admin shell.
 * @module @labcraft/admin/src/client/recalibration
 *
 * One `CoreLifecycle`-based namespacer per UI panel (per the workspace
 * Client-shape rule in `.agents/rules/services.md`). Owns no transport
 * of its own — the parent `Admin` adopts the shared `HttpTransport`
 * and hands a reference here at construction. Logs and metrics bubble
 * under `admin.recalibration.*`.
 */

import { CoreLifecycle } from '@labcraft/core'
import type { HttpTransport } from './index.ts'
import {
  RecalibrationContentPrRoute,
  RecalibrationDraftRoute,
  RecalibrationSignalRoute,
  type ContentPrArtifact,
  type RecalibrationDraft,
  type RecalibrationSignalResponse,
} from '../shared/recalibration_routes.ts'

/**
 * Read + draft + content-PR namespacer for the Recalibration panel.
 * Held on the frontend `Admin` Client; React components reach it via
 * `useClient<Admin>().recalibration`.
 */
export class Recalibration extends CoreLifecycle<{ id?: string }> {
  private readonly http: HttpTransport

  constructor(http: HttpTransport) {
    super({ id: 'recalibration' })
    this.http = http
  }

  /**
   * Fetch the anonymized mentor-capture signal (diagnosis+outcome
   * buckets + wrong-turn firing stats).
   */
  async signal(): Promise<RecalibrationSignalResponse> {
    return this.http.request(RecalibrationSignalRoute)
  }

  /**
   * Ask the daemon to draft recalibration proposals from the current
   * signal. Requestless POST — the daemon reads the signal itself.
   */
  async draft(): Promise<RecalibrationDraft> {
    return this.http.request(RecalibrationDraftRoute)
  }

  /**
   * Turn an approved draft into a content-PR instruction artifact.
   *
   * @param draft - The approved subset of proposals.
   */
  async contentPr(draft: RecalibrationDraft): Promise<ContentPrArtifact> {
    return this.http.request(RecalibrationContentPrRoute, { body: draft })
  }
}
